import { ContentViewSchema } from '@fsf/types';
import { _ } from '@ngx-translate/core';
import { z } from 'zod';

/**
 * Announcement surfaces. Mirrors `Content::TYPES` on the API — the public
 * site shows at most one announcement per surface (R29).
 */
export const CONTENT_TYPES = ['info_banner', 'popup', 'header_slider'] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

export type Content = z.infer<typeof ContentViewSchema>;

/**
 * The editor maintains exactly one text row, in the default language. The
 * translatable detail table stays in place on the API so a second language
 * can be added later without a migration — v1 simply has no picker.
 */
export const ContentDetailSchema = z.object({
  language: z.literal('deu'),
  headline: z.string().max(255).nullable().optional(),
  text: z.string().nullable().optional(),
  cta_text: z.string().max(255).nullable().optional(),
  cta_link: z
    .string()
    .max(255)
    .nullable()
    .optional()
    .refine(
      (value) =>
        !value ||
        (/^https:\/\/.+/.test(value) && !/[\s]/.test(value)) ||
        (value.startsWith('/') && !/^\/[/\\]/.test(value)),
      { message: 'content.announcements.validation.ctaLink' },
    ),
});

export const StoreContentSchema = z.object({
  type: z.enum(CONTENT_TYPES, { message: 'validation.invalidOption' }),
  valid_from: z.string().nullable().optional(),
  valid_until: z.string().nullable().optional(),
  rank: z.number().int().min(0),
  details: z.array(ContentDetailSchema).min(1),
});

export type StoreContentPayload = z.infer<typeof StoreContentSchema>;
export const UpdateContentSchema = StoreContentSchema.partial();
export type UpdateContentPayload = z.infer<typeof UpdateContentSchema>;

export const contentTranslations = _([
  'content.announcements.title',
  'content.announcements.add',
  'content.announcements.edit',
  'content.announcements.empty',
  'content.announcements.created',
  'content.announcements.updated',
  'content.announcements.deleted',
  'content.announcements.notFound',
  'content.announcements.loadError',
  'content.announcements.createError',
  'content.announcements.updateError',
  'content.announcements.deleteError',
  'content.announcements.deleteConfirmHeader',
  'content.announcements.deleteConfirmMessage',
  'content.announcements.dashboardDescription',
  'content.announcements.dashboardButton',
  'content.announcements.languageHint',
  'content.announcements.statusActive',
  'content.announcements.statusExpired',
  'content.announcements.statusScheduled',
  'content.announcements.imageSectionTitle',
  'content.announcements.imageHint',
  'content.announcements.imageUploaded',
  'content.announcements.imageUploadError',
  'content.announcements.imageRemoved',
  'content.announcements.imageRemoveError',
  'content.announcements.fields.type',
  'content.announcements.fields.rank',
  'content.announcements.fields.rankHint',
  'content.announcements.fields.validFrom',
  'content.announcements.fields.validFromHint',
  'content.announcements.fields.validUntil',
  'content.announcements.fields.validUntilHint',
  'content.announcements.fields.headline',
  'content.announcements.fields.text',
  'content.announcements.fields.ctaText',
  'content.announcements.fields.ctaLink',
  'content.announcements.fields.status',
  'content.announcements.types.info_banner',
  'content.announcements.types.popup',
  'content.announcements.types.header_slider',
  'content.announcements.validation.ctaLink',
]);
